/**
 * NotificationsTab - Notifications management
 * Extracted from MasterDashboard.jsx
 * Shows: notifications list, pending breaks, batch actions
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bell, Trash2, CheckCircle, XCircle } from "lucide-react";
import { HelpTooltip } from "@/components/HelpTooltip";
import AdminMessenger from "@/components/admin/AdminMessenger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); } catch (error) { return iso; }
};

const getNotificationIcon = (type) => {
  const icons = { checkin: "✓", checkout: "⏹", break_request: "☕", attendance_edit: "✏️", compensatory: "🍽️", overtime_enable: "⚡", support_reply: "💬" };
  return icons[type] || "📋";
};

export default function NotificationsTab({ companyId, userId, userName, onUnreadCountChange }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingBreaks, setPendingBreaks] = useState([]);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [selectAllNotifications, setSelectAllNotifications] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { params: { company_id: companyId } });
      setNotifications(response.data);
      const unread = response.data.filter(n => !n.read).length;
      setUnreadCount(unread);
      onUnreadCountChange?.(unread);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [companyId]);

  const fetchPendingBreaks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/breaks/pending`, { params: { company_id: companyId } });
      setPendingBreaks(response.data);
    } catch (error) {
      console.error("Error fetching breaks:", error);
    }
  }, [companyId]);

  useEffect(() => {
    fetchNotifications();
    fetchPendingBreaks();
    const interval = setInterval(() => { fetchNotifications(); fetchPendingBreaks(); }, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchPendingBreaks]);

  const handleMarkNotificationRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`);
      fetchNotifications();
    } catch (error) { toast.error("Eroare"); }
  };

  const handleToggleNotificationSelect = (notifId) => {
    setSelectedNotifications(prev => prev.includes(notifId) ? prev.filter(id => id !== notifId) : [...prev, notifId]);
  };

  const handleSelectAllNotifications = () => {
    if (selectAllNotifications) { setSelectedNotifications([]); }
    else { setSelectedNotifications(notifications.map(n => n.id)); }
    setSelectAllNotifications(!selectAllNotifications);
  };

  const handleDeleteSelectedNotifications = async () => {
    try {
      await Promise.all(selectedNotifications.map(id => axios.delete(`${API}/notifications/${id}`)));
      setSelectedNotifications([]);
      setSelectAllNotifications(false);
      fetchNotifications();
      toast.success(`${selectedNotifications.length} notificări șterse`);
    } catch (error) { toast.error("Eroare la ștergere"); }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, null, { params: { company_id: companyId } });
      fetchNotifications();
      toast.success("Toate notificările marcate ca citite");
    } catch (error) { toast.error("Eroare"); }
  };

  const handleDeleteAllNotifications = async () => {
    try {
      await axios.delete(`${API}/notifications/delete-all`, { params: { company_id: companyId } });
      fetchNotifications();
      toast.success("Toate notificările șterse");
    } catch (error) { toast.error("Eroare la ștergere"); }
  };

  const handleApproveBreak = async (breakId, approved) => {
    try {
      await axios.post(`${API}/breaks/approve`, { break_id: breakId, approved, approved_by: userId });
      toast.success(approved ? "Pauză aprobată" : "Pauză respinsă");
      fetchPendingBreaks();
      fetchNotifications();
    } catch (error) { toast.error("Eroare"); }
  };

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                <Bell className="w-6 h-6" />Notificări
                {unreadCount > 0 && <Badge className="bg-blue-500">{unreadCount}</Badge>}
                <HelpTooltip section="notificari" />
              </CardTitle>
              <CardDescription>Activitatea recentă a angajaților</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              {selectedNotifications.length > 0 && (
                <Button variant="destructive" size="sm" onClick={handleDeleteSelectedNotifications} data-testid="delete-selected-notifications">
                  <Trash2 className="w-4 h-4 mr-1" />Șterge ({selectedNotifications.length})
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleMarkAllNotificationsRead}>
                <CheckCircle className="w-4 h-4 mr-1" />Citite toate
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDeleteAllNotifications} className="text-red-600 hover:text-red-800">
                <Trash2 className="w-4 h-4 mr-1" />Șterge toate
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            {notifications.length === 0 ? (
              <p className="text-slate-500 text-center py-8">Nicio notificare</p>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-2 bg-slate-100 rounded-lg sticky top-0 z-10">
                  <Checkbox checked={selectAllNotifications} onCheckedChange={handleSelectAllNotifications} id="select-all-notif" />
                  <Label htmlFor="select-all-notif" className="text-sm font-medium cursor-pointer">Selectează toate ({notifications.length})</Label>
                </div>
                {notifications.map((notif) => (
                  <div key={notif.id} className={`p-4 rounded-xl border transition-all ${notif.read ? 'bg-slate-50 border-slate-200 opacity-70' : 'bg-white border-blue-300 shadow-md'} ${selectedNotifications.includes(notif.id) ? 'ring-2 ring-blue-500' : ''}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox checked={selectedNotifications.includes(notif.id)} onCheckedChange={() => handleToggleNotificationSelect(notif.id)} className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-2xl">{getNotificationIcon(notif.type)}</span>
                          <h4 className={`font-semibold text-lg ${notif.read ? 'text-slate-600' : 'text-slate-900'}`}>{notif.title}</h4>
                          {!notif.read && <Badge className="bg-blue-500 text-white">NOU</Badge>}
                        </div>
                        <p className={`text-sm mb-2 ${notif.read ? 'text-slate-500' : 'text-slate-700'}`}>{notif.message}</p>
                        {notif.type === 'support_reply' && notif.data?.reply && (
                          <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-xs text-green-600 font-semibold mb-1">Răspunsul echipei de suport:</p>
                            <p className="text-sm text-green-800">{notif.data.reply}</p>
                          </div>
                        )}
                        <p className="text-xs text-slate-500 mt-2">{formatDate(notif.created_at)}</p>
                      </div>
                      <div className="flex gap-1">
                        {!notif.read && (
                          <Button variant="ghost" size="sm" onClick={() => handleMarkNotificationRead(notif.id)} className="text-blue-600 hover:text-blue-800" title="Marchează ca citită">
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={async () => { try { await axios.delete(`${API}/notifications/${notif.id}`); fetchNotifications(); toast.success("Notificare ștearsă"); } catch (error) { toast.error("Eroare la ștergere"); } }} className="text-red-500 hover:text-red-700" title="Șterge notificare">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Pending Breaks */}
      {pendingBreaks.length > 0 && (
        <Card className="border-0 shadow-lg border-l-4 border-l-orange-500">
          <CardHeader>
            <CardTitle className="text-xl text-orange-700">Cereri Pauză Pendente ({pendingBreaks.length})</CardTitle>
            <CardDescription>Necesită aprobare urgentă</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingBreaks.map((brk) => (
                <div key={brk.id} className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-lg">{brk.user_name}</p>
                      <p className="text-sm text-slate-600">Durată: {brk.duration_minutes} minute</p>
                      <p className="text-sm text-slate-600">Motiv: {brk.reason || 'Nespecificat'}</p>
                      <p className="text-xs text-slate-500 mt-1">Solicitat: {formatDate(brk.requested_at)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApproveBreak(brk.id, true)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Aprobă
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleApproveBreak(brk.id, false)}>
                        <XCircle className="w-4 h-4 mr-1" />Respinge
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Admin Messenger - Mesaje de la angajați */}
      <AdminMessenger 
        companyId={companyId}
        adminId={userId}
        adminName={userName}
      />
    </div>
  );
}
